import classNames from "classnames"
import { FieldConfig, FieldMetaProps, FieldProps, FormikErrors } from "formik"
import { isNumber } from "lodash"
import { I18n } from "next-translate"
import React, { ReactElement } from "react"
import { FormText } from "reactstrap"

import { errorIdForField, INVALID_FORM_INPUT_CLASSNAME } from "@basics/form"
import { addNamespacePrefixIfNotPresent } from "@services/i18n/i18n"

import { IFAQPopupProps } from "../faq/FAQPopup"
import HtmlContent from "../HtmlContent"
import TranslatedHtml from "../TranslatedHtml"

/** Formik setFieldValue has this specific type, e.g. the FormType could be IProgram */
export type SetFieldValueType<FormType = any> = (field: string, value: any, shouldValidate?: boolean) => Promise<void | FormikErrors<FormType>>

export type FieldComponentType = FieldConfig["component"]

export interface IBaseFormikProps extends FieldProps {
  /**
   * Help text to be shown to the user.
   */
  help?: string
  /**
   * The i18n interface to make translations
   * // TODO rework to use dynamicTranslation
   */
  i18n: I18n
  id?: string
  /**
   * Label/title to be shown to the user.
   */
  label?: string
  /**
   * Name of the field: usually matches the property name of an entity.
   */
  name: string
  /**
   * Is a field input required?
   */
  required?: boolean
  /**
   * Should the inputted help text be translated before shown to the user?
   */
  translateHelp?: boolean
  /**
   * Should the inputted label be translated before shown to the user?
   */
  translateLabel?: boolean
  /**
   * Role of the Formik element, e.g. "input"
   */
  role?: string
  /*
 * NOTE: there is no type checking for this prop if a Formik component with FAQ popup is "embedded"
 * in way of <Field component={...} (instead of using <FAQPopup ...> directly).
 * In this cases coders should use explicit typing with `as IFAQPopupProps`.
 * See doc/principles/forms.md for more information as well as https://futureprojects.atlassian.net/browse/FCP-1125
 */
  faqpopup?: IFAQPopupProps
  /**
   * To display an error for the element within a group,
   * i.e., each element has the same name but a different value. Commonly used for checkboxes.
   * For example, used in the UserEmailForm to display a possible error message for each receiver.
   */
  groupIndex?: number
}

/**
 * Offers a standard for showing errors, help and a label.
 *
 * If the field is required and the label is not empty adding an asterisk at the of the label
 */
abstract class FormikElement<Props extends IBaseFormikProps> extends React.Component<Props> {
  /**
   * @returns the meta props of the field, recognized by the field name.
   */
  protected meta = (): FieldMetaProps<unknown> => this.props.form.getFieldMeta(this.props.field.name)

  /**
   * @returns if there is an error connected to the field.
   */
  protected hasError = (): boolean => !!this.error()

  /**
   * The error to the connected field, if any exists.
   */
  private error = (): string =>
    // if an error exists and
    this.meta().error
      // ...the error is of type object
      && typeof this.meta().error === "object"
      // ...exists a groupIndex and it is a number
      && isNumber(this.props.groupIndex)
      // ...and the groupIndex is found in the error object
      && Object.keys(this.meta().error)?.includes(this.props.groupIndex.toString())
      // ...returns the error from the connection group element identified by the group index
      ? this.meta().error[this.props.groupIndex]
      // otherwise if also an eror exists and is of type string
      : this.meta().error && typeof this.meta().error === "string"
        // ...return the error
        ? this.meta().error
        // otherwise, return undefined, b/c no error exists
        : undefined

  /**
   * @returns the label (title) text of the field
   */
  protected labelText(): string {
    // render no <label> when an empty label was provided,
    // if no label was given fallback to the element name
    let label = typeof this.props.label === "undefined"
      ? this.props.field.name.charAt(0).toUpperCase() + this.props.field.name.slice(1)
      : this.props.label

    // translate now, maybe a translation string was given but the translation was empty
    // -> don't show
    if (label.length > 0
      // if the given label was already translated or should not be translated because it is a
      // user-entered string: don't try to translate, this will possibly strip some text if the
      // string contains ":" or "." (i18n-next keySeparator, nsSeparator)
      && (typeof this.props.translateLabel === "undefined" || this.props.translateLabel === true)
    ) {
      label = this.props.i18n.t(label)
    }

    if (label.length > 0 && this.props.required) {
      label = label + " *"
    }

    return label
  }

  /**
   * Returns an html element if there is an error for that field.
   */
  protected errorElement = (): ReactElement => {
    const className = classNames(
      INVALID_FORM_INPUT_CLASSNAME,
      "text-danger",
      "was-validated",
    )

    return this.hasError() && <div
      id={errorIdForField(this.props.name || this.props.field.name)}
      className={className}
    >
      <TranslatedHtml content={addNamespacePrefixIfNotPresent("error", this.error())} />
    </div>
  }

  /**
   * @returns the help text of this field
   */
  protected helpElement = (): ReactElement => {
    let helpText = this.props.help
    if (typeof helpText === "undefined" || !helpText.length) {
      return null
    }

    // if the given help was already translated or should not be translated because it is a
    // user-entered string: don't try to translate, this will possibly strip some text if the
    // string contains ":" or "." (i18n-next keySeparator, nsSeparator)
    if (typeof this.props.translateHelp === "undefined" || this.props.translateHelp === true) {
      helpText = this.props.i18n.t(helpText)
    }

    // check length again, maybe the translation is empty
    return helpText.length
      ? <FormText>
        <HtmlContent content={helpText} />
      </FormText>
      : null
  }
}

export default FormikElement
